import {
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Circle,
  Flex,
  HStack,
  Heading,
  Icon,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LucideArrowRight, LucideTriangleAlert } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import type { ReactElement } from "react";
import { z } from "zod";

import { useSession } from "@chainpatrol/auth/react";
import { OrganizationPortalVisibility } from "@chainpatrol/database/enums";

import { PublicMetrics } from "~/components/PublicMetrics";
import { ShellForOrg } from "~/layouts/ShellForOrg";
import { NotFoundError, UnauthorizedError } from "~/lib/error";
import { trpc } from "~/lib/trpc";

const schema = z
  .object({
    slug: z.string().nullable(),
  })
  .nullable();

function SecurityPortalPage() {
  const params = useParams();
  const parsedParams = schema.parse(params);
  const slug = parsedParams?.slug;
  const session = useSession();

  const getOrgBySlugPublicQuery = trpc.organization.getBySlugPublic.useQuery(
    { slug: slug as string },
    { enabled: !!slug && session.status === "authenticated" },
  );

  if (session.status === "loading" || getOrgBySlugPublicQuery.status === "pending") {
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );
  }

  const org = getOrgBySlugPublicQuery.data;

  if (!org || org.portalVisibility === OrganizationPortalVisibility.DISABLED) {
    throw new NotFoundError("Organization not found");
  }

  if (
    org.portalVisibility === OrganizationPortalVisibility.PRIVATE &&
    !session.data?.user?.orgIds?.includes(org.id)
  ) {
    throw new UnauthorizedError("You do not have access to this organization");
  }

  return (
    <Stack py="1" spacing={50}>
      <Flex gap={8} flexWrap="wrap">
        <Card
          flex="1 1 300px"
          direction="column"
          overflow="hidden"
          bgGradient="linear(to-tr, blackAlpha.500, blackAlpha.500, orange.900)">
          <CardBody h="full">
            <Stack gap={4} h="full" alignItems="flex-start">
              <Box flex="1">
                <HStack>
                  <Circle bgColor="whiteAlpha.100" size={8}>
                    <Icon as={LucideTriangleAlert} boxSize={4} color="whiteAlpha.900" />
                  </Circle>
                  <Heading fontSize={20}>Report a Threat</Heading>
                </HStack>
                <Text color="gray.300">
                  Submit a report that will be sent directly to the {org.name} team. These
                  reports are reviewed by staff to make sure we block threats as quickly
                  as possible
                </Text>
              </Box>
              <Link href={`/${org.slug}/report`}>
                <Button
                  colorScheme="orange"
                  variant="secondary"
                  size="sm"
                  rightIcon={<Icon as={LucideArrowRight} />}>
                  Report
                </Button>
              </Link>
            </Stack>
          </CardBody>
        </Card>
      </Flex>
      {(slug === "curve" || slug === "gitcoin" || slug === "zenrock") && (
        <PublicMetrics />
      )}
    </Stack>
  );
}

SecurityPortalPage.getLayout = function getLayout(page: ReactElement) {
  return <ShellForOrg>{page}</ShellForOrg>;
};

export default SecurityPortalPage;
